import { SettingsEditor } from "./SettingsEditor";

export function SettingsPage() {
  return (
    <SettingsEditor
      title="App Settings"
      keyPrefix="app."
      helperText="General app-wide configuration values, stored in app_settings and readable by the app. Keys here should start with 'app.' by convention (e.g. app.maintenance_mode)."
    />
  );
}

export function AdsConfigPage() {
  return (
    <SettingsEditor
      title="Rewarded Ads Configuration"
      keyPrefix="ads."
      helperText="Ad-related config values (e.g. ads.rewarded_coin_amount). Note: the Android app currently reads AdMob unit IDs from its own build config, not from here yet — values saved here are real and persisted, but not yet consumed by the app at runtime."
    />
  );
}

/**
 * Round 21 point 7 — the real Admin Panel control for the gift-receiving
 * percentage (economy.gift_receiving_percentage, migration 0050), plus
 * economy.coins_per_dollar (already used by the withdrawal flow — see
 * routes/coins.js) which previously had no Admin Panel page of its own
 * either. Same generic app_settings editor as SettingsPage/AdsConfigPage,
 * just scoped to the "economy." key prefix — routes/coins.js reads
 * whatever value is saved here on every gift send; the Worker/DB always
 * fall back to a safe default (70%) if this key is ever missing, but the
 * Worker never uses anything BUT this stored value once it's set.
 */
export function EconomyConfigPage() {
  return (
    <SettingsEditor
      title="Economy Settings"
      keyPrefix="economy."
      helperText="Gift-economy configuration read directly by the Worker on every transaction — never hardcoded and never trusted from Android. economy.gift_receiving_percentage (0-100) is the percentage of a gift's coin value credited to the receiver as diamonds; economy.coins_per_dollar controls coin withdrawal value. Changes here take effect on the very next gift/withdrawal — no redeploy needed."
    />
  );
}

/**
 * Round 45 follow-up, item 12 — "Configurable system-message sender
 * account." Same generic app_settings editor as the pages above, scoped
 * to system_sender. — see lib/system-sender.js's own doc for exactly how
 * these two keys are read and used (coin/medal/mall-item/daily-signin
 * reward notifications, and gating the Message button on that account's
 * profile).
 */
export function SystemSenderConfigPage() {
  return (
    <SettingsEditor
      title="System Sender Account"
      keyPrefix="system_sender."
      helperText={
        'Set system_sender.public_user_id to the sender account\'s public User ID (the numeric id shown on a ' +
        'profile — not the internal account id). That real account\'s username/avatar then appears as the sender ' +
        'on every coin/medal/mall-item/daily-signin reward notification. Set system_sender.allow_replies to ' +
        '"true" to let users message that account back, or "false" (or leave it unset) to hide the Message ' +
        "option on that account's profile — the default, since this is meant to be a one-way notification " +
        "account. Both keys are optional: until public_user_id is set, reward actions simply skip sending a " +
        "notification (logged, never blocking the actual coin/medal/item grant)."
      }
    />
  );
}

/**
 * Round 52 — security hardening controls. Same generic app_settings editor,
 * scoped to security. — read by the Worker (lib/app-attest.js, cached ~30 s)
 * and by the database triggers from migration 0126.
 */
export function SecurityConfigPage() {
  return (
    <SettingsEditor
      title="Security & App Integrity"
      keyPrefix="security."
      helperText={
        "security.attest_mode and security.replay_mode take off | monitor | enforce. Start in monitor: the server verifies " +
        "and logs but blocks nobody. Switch attest_mode to enforce only after the Play Integrity setup is complete and " +
        "the monitor logs show real users passing — enforce rejects every sensitive request (Worker and database) that " +
        "lacks a valid app-integrity token, including users on old app versions. security.block_proxy / block_vpn / " +
        "block_root / block_non_play_install tell the app to show 'Security Check Failed' locally; they are advisory " +
        "signals that a modified client can bypass, so VPN, root and non-Play install are off by default to avoid " +
        "locking out legitimate users (corporate VPNs, private DNS, accessibility tools). security.min_device_integrity " +
        "is MEETS_BASIC_INTEGRITY | MEETS_DEVICE_INTEGRITY | MEETS_STRONG_INTEGRITY."
      }
    />
  );
}
